import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './mikumi.css';
import {
  IMAGE_FALLBACK,
  MIKUMI_HERO_SLIDES,
  MIKUMI_PAGE_IMAGES,
} from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import giraffeOverview from '../../assets/images/hero-giraffe.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';

const MIKUMI_IMAGES = MIKUMI_PAGE_IMAGES;

const HERO_STATS = [
  { value: '3,230', label: 'km² of wilderness', delay: 0 },
  { value: '4 hrs', label: 'From Dar es Salaam', delay: 80 },
  { value: '400+', label: 'Bird species', delay: 160 },
  { value: '1964', label: 'Established', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: lionFace,
    title: 'Mkata Lions',
    text: 'Lions hunt and rest on the open Mkata floodplain often visible from the main road at close range.',
    facePosition: 'center 38%',
  },
  {
    animalFace: elephantFace,
    title: 'Elephant Herds',
    text: 'Large herds move through baobab-dotted savanna and riverine woodland, a hallmark of southern Tanzania.',
    facePosition: 'center 42%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Zebras & Wildebeest',
    text: 'Vast grazing herds mirror the Serengeti on a smaller scale earning Mikumi the nickname "mini-Serengeti".',
    facePosition: 'center 35%',
  },
  {
    animalFace: giraffeOverview,
    title: 'Giraffes',
    text: 'Towering giraffes browse acacia canopies along the floodplain edges and open woodland corridors.',
    facePosition: 'center 30%',
  },
  {
    animalFace: wilddogFace,
    title: 'Hippos & Crocodiles',
    text: 'Hippo pools and river channels hold crocodiles and waterbirds rewarding stops between game drives.',
    facePosition: 'center 36%',
  },
  {
    animalFace: eagleFace,
    title: 'Birdlife',
    text: 'Over 400 species including lilac-breasted rollers, hornbills, and raptors thrive across the park.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Wildlife gathers around remaining water sources. Clear skies and excellent visibility on the Mkata plains.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush grasslands, newborn antelope, and vibrant birdlife fewer visitors and softer light for photography.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains bring dramatic skies and lower rates. Morning drives are often productive before afternoon showers.',
  },
];

const EXPERIENCES = [
  {
    title: 'Mkata Floodplain Drive',
    text: 'Traverse the park\'s signature grasslands where elephants, lions, and zebra herds share open savanna vistas.',
  },
  {
    title: 'Baobab & Savanna Scenery',
    text: 'Wind through iconic baobab groves and golden plains landscapes that feel like a condensed northern safari.',
  },
  {
    title: 'Southern Circuit Extension',
    text: 'Combine Mikumi with Ruaha or Udzungwa Mountains for a multi-park journey through Tanzania\'s wild south.',
  },
];

const GALLERY_IMAGES = [
  { src: MIKUMI_IMAGES.classic, caption: 'Mikumi National Park' },
  { src: MIKUMI_IMAGES.zebra, caption: 'Zebra on the Plains' },
  { src: MIKUMI_IMAGES.gazelles, caption: 'Gazelles on the Grasslands' },
  { src: MIKUMI_IMAGES.warthog, caption: 'Warthog at Rest' },
  { src: MIKUMI_IMAGES.baboon, caption: 'Baboon on the Plains' },
  { src: MIKUMI_IMAGES.plains, caption: 'Mkata Floodplain' },
];

const SAFARI_PACKAGES = [
  {
    image: MIKUMI_IMAGES.plains,
    title: 'Mikumi Day Safari',
    duration: '1 day',
    tag: 'Day trip',
    text: 'A full day from Dar es Salaam or Morogoro across the Mkata floodplain elephants, lions, and zebra on Tanzania\'s most accessible safari.',
    highlights: ['Dar / Morogoro pickup', 'Full-day game drive', 'Picnic lunch'],
  },
  {
    image: MIKUMI_IMAGES.savanna,
    title: 'Mikumi Overnight Escape',
    duration: '2–3 days',
    tag: 'Short safari',
    text: 'Stay near the park for dawn and dusk drives through baobab savanna quieter crowds than the north, classic big-game viewing.',
    highlights: ['Park lodge / camp', 'Sunrise drives', 'Mkata plains wildlife'],
  },
  {
    image: MIKUMI_IMAGES.wildlife,
    title: 'Mikumi & Udzungwa Combo',
    duration: '3–4 days',
    tag: 'Classic combo',
    text: 'Pair Mikumi\'s open plains with Udzungwa\'s rainforest trails and Sanje Falls two contrasting southern highlights.',
    highlights: ['Savanna + forest', 'Waterfall hike', 'Flexible lodges'],
  },
  {
    image: MIKUMI_IMAGES.road,
    title: 'Southern Circuit Gateway',
    duration: '6–8 days',
    tag: 'Most popular',
    text: 'Begin in Mikumi, then continue to Nyerere, Ruaha, or Udzungwa the classic opener for Tanzania\'s wild south.',
    highlights: ['Multi-park itinerary', 'Private 4×4', 'Remote southern parks'],
  },
];

const GETTING_THERE_ROUTES = [
  'Drive from Dar es Salaam via Morogoro on tarmac (approx. 4–5 hrs) Tanzania\'s most accessible safari park',
  'Continue west to Ruaha National Park or Udzungwa Mountains for a extended Southern Circuit itinerary',
  'Fly into Dar es Salaam or Julius Nyerere International Airport, then road transfer with a private guide',
  'Ideal first safari for travellers starting from the coast before heading to Zanzibar or the north',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="mikumi-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="mikumi-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="mikumi-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="mikumi-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Mikumi() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="mikumi">
      <section className="mikumi-hero" aria-labelledby="mikumi-hero-heading">
        <div className="mikumi-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="mikumi-hero" slides={MIKUMI_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="mikumi-hero__overlay" />
        </div>
        <div className="page-container mikumi-hero__container">
          <div className="mikumi-hero__content">
            <nav className="mikumi-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="mikumi-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#tanzania-mainland">Mainland</a>
              <span className="mikumi-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Mikumi</span>
            </nav>
            <p className="mikumi-hero__eyebrow">Southern Safari Circuit</p>
            <h1 id="mikumi-hero-heading">
              Mikumi
              <span>National Park</span>
            </h1>
            <p className="mikumi-hero__lead">
              Tanzania&apos;s most accessible wilderness open savanna, baobab
              woodlands, and big-game viewing just hours from Dar es Salaam.
            </p>
            <div className="mikumi-hero__actions">
              <a className="mikumi-btn mikumi-btn--primary" href="#contact">
                Plan your Mikumi safari
              </a>
              <a className="mikumi-btn mikumi-btn--outline" href="#mikumi-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="mikumi-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="mikumi-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="mikumi-hero__stat-value">{stat.value}</span>
              <span className="mikumi-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="mikumi-overview"
        className="mikumi-section mikumi-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="mikumi-overview__grid">
            <Reveal className="mikumi-overview__text">
              <p className="mikumi-section__eyebrow">About the park</p>
              <h2 id="overview-heading">The gateway to southern Tanzania</h2>
              <p>
                Mikumi National Park lies along the Tanzam Highway between
                Morogoro and Iringa, making it the easiest safari destination
                to reach from Dar es Salaam. The Mkata floodplain dominates the
                landscape wide grasslands where wildlife is often visible
                from the road itself.
              </p>
              <p>
                Often compared to a miniature Serengeti, Mikumi offers
                year-round game viewing without the long flights or drives
                required for the Northern Circuit. Pair it with Udzungwa&apos;s
                rainforest hikes or Ruaha&apos;s remote wilderness for a complete
                Southern Circuit adventure.
              </p>
              <ul className="mikumi-overview__details">
                <Reveal as="li" className="mikumi-overview__detail" delay={0}>
                  <span className="mikumi-overview__detail-label">Location</span>
                  <span className="mikumi-overview__detail-value">Southern Tanzania</span>
                </Reveal>
                <Reveal as="li" className="mikumi-overview__detail" delay={80}>
                  <span className="mikumi-overview__detail-label">Park size</span>
                  <span className="mikumi-overview__detail-value">3,230 km²</span>
                </Reveal>
                <Reveal as="li" className="mikumi-overview__detail" delay={160}>
                  <span className="mikumi-overview__detail-label">Established</span>
                  <span className="mikumi-overview__detail-value">1964</span>
                </Reveal>
                <Reveal as="li" className="mikumi-overview__detail" delay={240}>
                  <span className="mikumi-overview__detail-label">Best for</span>
                  <span className="mikumi-overview__detail-value">Easy access &amp; big game</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="mikumi-overview__image" variant="right" delay={200}>
              <SafeImage
                className="mikumi-overview__img"
                src={MIKUMI_IMAGES.overview}
                alt="Giraffe on the African savanna in Mikumi National Park"
              />
              <span className="mikumi-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="mikumi-packages"
        className="mikumi-section mikumi-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="mikumi-section__header">
            <p className="mikumi-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Mikumi safari packages</h2>
            <p className="mikumi-section__intro">
              From a day trip out of Dar es Salaam to a full Southern Circuit
              journey each package is tailored to your dates, lodge style, and pace.
            </p>
          </Reveal>
          <ul className="mikumi-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="mikumi-packages__card"
                delay={index * 100}
              >
                <div className="mikumi-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="mikumi-packages__duration">{pkg.duration}</span>
                </div>
                <div className="mikumi-packages__card-body">
                  <span className="mikumi-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="mikumi-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="mikumi-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="mikumi-wildlife"
        className="mikumi-section mikumi-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="mikumi-section__header">
            <p className="mikumi-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Life on the Mkata plains</h2>
            <p className="mikumi-section__intro">
              Elephants, predators, and grazing herds share open grasslands and
              riverine woodland rewarding game drives in every season.
            </p>
          </Reveal>
          <ul className="mikumi-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="mikumi-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="mikumi-wildlife__icon-frame">
                  <span className="mikumi-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="mikumi-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="mikumi-gallery"
        className="mikumi-section mikumi-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="mikumi-section__header">
            <p className="mikumi-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Mikumi</h2>
            <p className="mikumi-section__intro">
              From baobab-studded horizons to close encounters on the floodplain
              the southern savanna in every light.
            </p>
          </Reveal>
          <div className="mikumi-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="mikumi-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="mikumi-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="mikumi-seasons"
        className="mikumi-section mikumi-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="mikumi-section__header">
            <p className="mikumi-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Mikumi</h2>
            <p className="mikumi-section__intro">
              Mikumi delivers strong wildlife viewing year-round pick your
              season for dry-season concentrations or green-season landscapes.
            </p>
          </Reveal>
          <ul className="mikumi-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="mikumi-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`mikumi-seasons__card-badge mikumi-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="mikumi-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="mikumi-experiences"
        className="mikumi-section mikumi-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="mikumi-section__header">
            <p className="mikumi-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Mikumi</h2>
            <p className="mikumi-section__intro">
              From classic game drives to multi-park Southern Circuit routes
              every itinerary starts with the open plains of Mikumi.
            </p>
          </Reveal>
          <ul className="mikumi-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="mikumi-experiences__card mikumi-experiences__card--text"
                delay={index * 120}
              >
                <div className="mikumi-experiences__card-body">
                  <span className="mikumi-experiences__index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="mikumi-getting-there"
        className="mikumi-section mikumi-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="mikumi-getting-there__inner">
            <Reveal className="mikumi-getting-there__copy">
              <p className="mikumi-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Mikumi</h2>
              <p>
                Mikumi sits on the main highway between Dar es Salaam and the
                southern highlands. Most visitors drive in for a day or two
                before continuing to Ruaha, Udzungwa, or the coast.
              </p>
              <ul className="mikumi-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="mikumi-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Mikumi National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168.089654321!2d37.0!3d-7.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185b8b8b8b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2sMikumi%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mikumi-cta" aria-labelledby="mikumi-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="mikumi-cta__inner">
              <h2 id="mikumi-cta-heading">
                Ready to explore the southern savanna?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Mikumi safari with game drives, lodge stays, and Southern
                Circuit connections.
              </p>
              <a
                className="mikumi-btn mikumi-btn--primary mikumi-btn--lg"
                href="#contact"
              >
                Get a free Mikumi quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Mikumi;
